import { Routes, Route, Navigate } from 'react-router';
import { AdminLayoutWrapper } from './components/AdminLayoutWrapper';
import AdminDashboard from './pages/AdminDashboard';
import CustomersListPage from './pages/customers/CustomersListPage';
import ImportExistingCustomerPage from './pages/customers/ImportExistingCustomerPage';
import CustomerDetailPage from './pages/customers/CustomerDetailPage';
import CustomerOverviewTab from './pages/customers/detail/CustomerOverviewTab';
import CustomerHostingTab from './pages/customers/detail/CustomerHostingTab';
import CustomerDomainsTab from './pages/customers/detail/CustomerDomainsTab';
import CustomerBillingTab from './pages/customers/detail/CustomerBillingTab';
import CustomerActivityTab from './pages/customers/detail/CustomerActivityTab';
import ProvisioningQueuePage from './pages/ops/ProvisioningQueuePage';
import AuditLogPage from './pages/ops/AuditLogPage';
import BulkActionsHistoryPage from './pages/ops/BulkActionsHistoryPage';
import CronHealthPage from './pages/ops/CronHealthPage';
import ActionQueuePage from './pages/ops/ActionQueuePage';
import NotificationsLogPage from './pages/ops/NotificationsLogPage';
import SupportInboxPage from './pages/support/SupportInboxPage';
import TicketDetailPage from './pages/support/TicketDetailPage';
import AdminDomainsListPage from './pages/domains/AdminDomainsListPage';
import AdminDomainDetailPage from './pages/domains/AdminDomainDetailPage';
import AdminDomainsTransfersPage from './pages/domains/AdminDomainsTransfersPage';
import AdminDomainReconciliationPage from './pages/domains/AdminDomainReconciliationPage';
import ReportsOverviewPage from './pages/reports/ReportsOverviewPage';
import LifecycleFunnelPage from './pages/reports/LifecycleFunnelPage';
import ProvisioningHealthPage from './pages/reports/ProvisioningHealthPage';
import SupportSlaPage from './pages/reports/SupportSlaPage';
import DomainsReportPage from './pages/reports/DomainsReportPage';
import AcquisitionPage from './pages/reports/AcquisitionPage';
import ServicesReportPage from './pages/reports/ServicesReportPage';
import MonthlyStatementPage from './pages/reports/financials/MonthlyStatementPage';
import TwelveMonthReportPage from './pages/reports/financials/TwelveMonthReportPage';
import YearlyReportPage from './pages/reports/financials/YearlyReportPage';
import ProjectionsPage from './pages/reports/financials/ProjectionsPage';
import ExpensesLedgerPage from './pages/reports/financials/ExpensesLedgerPage';
import SystemSettingsPage from './pages/settings/SystemSettingsPage';
import PlansPage from './pages/settings/PlansPage';
import AdminInvoicesPage from './pages/billing/AdminInvoicesPage';
import AdminDunningPage from './pages/billing/AdminDunningPage';
import AdminRecoupmentPage from './pages/billing/AdminRecoupmentPage';
import AdminPendingOrdersPage from './pages/billing/AdminPendingOrdersPage';
import AdminProofsOfPaymentPage from './pages/billing/AdminProofsOfPaymentPage';
import AdminPaymentsPage from './pages/billing/AdminPaymentsPage';
import AdminCreditNotesPage from './pages/billing/AdminCreditNotesPage';
import InstallmentRecoveryPage from './pages/services/InstallmentRecoveryPage';
import AdminServiceOrdersPage from './pages/services/AdminServiceOrdersPage';
import ServiceOrderDetailPage from './pages/services/ServiceOrderDetailPage';
import CareSubscriptionsPage from './pages/services/CareSubscriptionsPage';
import AdminAccountsPage from './pages/hosting/AdminAccountsPage';
import AdminServerHealthPage from './pages/hosting/AdminServerHealthPage';
import AdminTerminationsPage from './pages/hosting/AdminTerminationsPage';
import StaffPage from './pages/settings/StaffPage';
import EmailTemplatesPage from './pages/settings/EmailTemplatesPage';
import AnnouncementsPage from './pages/settings/AnnouncementsPage';

/**
 * AdminRouter — route tree for /admin/*. Mounted by App.tsx behind a
 * StaffRoute guard.
 */
export function AdminRouter() {
  return (
    <Routes>
      <Route element={<AdminLayoutWrapper />}>
        <Route index element={<AdminDashboard />} />
        <Route path="customers" element={<CustomersListPage />} />
        {/* 2026-09-29 (AR-OI-11): the admin New Customer page provisioned hosting with no
            domain registration and no invoice (Cutting Room). Staff add customers
            through the sales flow, which bills first. Kept as a redirect so
            bookmarks never fall through to customers/:id with id="new". */}
        <Route path="customers/new" element={<Navigate to="/sales/customers/new" replace />} />
        <Route
          path="customers/import-existing"
          element={<ImportExistingCustomerPage />}
        />

        {/* Customer detail — record-scoped tabs (lazy-loaded per tab) */}
        <Route path="customers/:id" element={<CustomerDetailPage />}>
          <Route index element={<CustomerOverviewTab />} />
          <Route path="hosting" element={<CustomerHostingTab />} />
          <Route path="domains" element={<CustomerDomainsTab />} />
          <Route path="billing" element={<CustomerBillingTab />} />
          <Route path="activity" element={<CustomerActivityTab />} />
        </Route>

        {/* Ops */}
        {/* Ops has no landing page of its own; the action queue is the
            reason anyone opens the section. The redirect exists so the top-nav
            item can point at /admin/ops and therefore stay highlighted across
            all six Ops pages — pointing it straight at action-queue would light
            the item on one page and go dark on the other five. */}
        <Route path="ops" element={<Navigate to="/admin/ops/action-queue" replace />} />
        <Route path="ops/action-queue" element={<ActionQueuePage />} />
        <Route path="ops/provisioning" element={<ProvisioningQueuePage />} />
        <Route path="ops/audit" element={<AuditLogPage />} />
        <Route path="ops/bulk-actions" element={<BulkActionsHistoryPage />} />
        <Route path="ops/cron-health" element={<CronHealthPage />} />
        <Route path="ops/notifications" element={<NotificationsLogPage />} />

        {/* Support inbox + ticket detail (Phase 8d) */}
        <Route path="support" element={<SupportInboxPage />} />
        <Route path="support/:id" element={<TicketDetailPage />} />

        {/* Hosting (Phase 8i — cross-customer admin views) */}
        <Route path="hosting" element={<AdminAccountsPage />} />
        <Route path="hosting/server-health" element={<AdminServerHealthPage />} />
        <Route path="hosting/terminations" element={<AdminTerminationsPage />} />

        {/* Domains (Phase 8e.4 — read-only) */}
        <Route path="domains" element={<AdminDomainsListPage />} />
        <Route path="domains/expiring" element={<AdminDomainsListPage />} />
        <Route path="domains/transfers" element={<AdminDomainsTransfersPage />} />
        {/* Sprint Admin-A A-2 — reconciliation must come BEFORE :domain so
            "reconcile" isn't treated as a domain name. */}
        <Route path="domains/reconcile" element={<AdminDomainReconciliationPage />} />
        <Route path="domains/:domain" element={<AdminDomainDetailPage />} />

        {/* Reports (Phase 8f) */}
        <Route path="reports" element={<ReportsOverviewPage />} />
        <Route path="reports/lifecycle" element={<LifecycleFunnelPage />} />
        <Route path="reports/provisioning" element={<ProvisioningHealthPage />} />
        <Route path="reports/support" element={<SupportSlaPage />} />
        <Route path="reports/domains" element={<DomainsReportPage />} />
        <Route path="reports/services" element={<ServicesReportPage />} />
        <Route path="reports/acquisition" element={<AcquisitionPage />} />
        {/* AR-11: Financials is now a top-level SECTION, not a third level
            under Reports. Reports was doing two jobs — operational reporting
            and money — and carrying twelve destinations to do it. Splitting
            lands both inside the 3-7 guidance and removes the only genuine
            three-level violation in the console. */}
        <Route path="financials" element={<MonthlyStatementPage />} />
        <Route path="financials/12-month" element={<TwelveMonthReportPage />} />
        <Route path="financials/yearly" element={<YearlyReportPage />} />
        <Route path="financials/projections" element={<ProjectionsPage />} />
        <Route path="financials/expenses" element={<ExpensesLedgerPage />} />
        {/* Old bookmarks. The catch-all would send these to the dashboard,
            which is rude for a URL that worked yesterday. */}
        <Route path="reports/financials/*" element={<Navigate to="/admin/financials" replace />} />

        {/* Settings (Phase 8g) */}
        <Route path="settings" element={<SystemSettingsPage />} />
        <Route path="settings/plans" element={<PlansPage />} />
        <Route path="settings/staff" element={<StaffPage />} />
        <Route path="settings/email-templates" element={<EmailTemplatesPage />} />
        <Route path="settings/announcements" element={<AnnouncementsPage />} />

        {/* Billing (Phase 8h) */}
        <Route path="billing" element={<AdminInvoicesPage />} />
        <Route path="billing/pending-orders" element={<AdminPendingOrdersPage />} />
        <Route path="billing/proofs" element={<AdminProofsOfPaymentPage />} />
        <Route path="billing/payments" element={<AdminPaymentsPage />} />
        <Route path="billing/credit-notes" element={<AdminCreditNotesPage />} />
        {/* Slice 2 (2026-09-10): who is in dunning and what happens next (BRD D-6). */}
        <Route path="billing/dunning" element={<AdminDunningPage />} />
        {/* 2026-09-25 (domain gap G9): Terms 6.4(c) recoupment cases — the view had no screen. */}
        <Route path="billing/recoupment" element={<AdminRecoupmentPage />} />

        {/* Phase 10 Services */}
        <Route path="care" element={<CareSubscriptionsPage />} />
        {/* AR-10: the list must exist at the detail route's own prefix.
            Declared BEFORE :id so "installment-recovery" is never read as an id. */}
        <Route path="services" element={<AdminServiceOrdersPage />} />
        <Route path="services/installment-recovery" element={<InstallmentRecoveryPage />} />
        <Route path="services/:id" element={<ServiceOrderDetailPage />} />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}
